import { IconButton, Stack } from '@chronosphereio/chrono-ui';
import { DeleteOutline as DeleteIcon } from 'mdi-material-ui';
import { TableSortOption, TableSort as TableSortSchema } from '@/core';
import { OptionsEditorControl, SettingsAutocomplete, SortAutocomplete } from '@/plugin-editor-components';

const SORT_AUTOCOMPLETE_WIDTH = 144;

export const TABLE_SORT_OPTIONS: Array<{ id: TableSortOption; label: string; description?: string }> = [
  { id: 'default', label: 'Default', description: 'Original data order' },
  { id: 'asc', label: 'Ascending', description: 'Lowest to highest value' },
  { id: 'desc', label: 'Descending', description: 'Highest to lowest value' },
];

interface TableSortProps {
  sortByOptions: Array<{ id: string; label: string }>;
  onChange: (sort: TableSortSchema) => void;
  sort?: TableSortSchema;
  onDelete?: () => void;
}

export function TableSort({ onChange, sort, sortByOptions, onDelete }: TableSortProps) {
  const handleSortByChange = (_: unknown, sortBy: { id: string; label: string } | null) => {
    onChange({ sortBy: sortBy?.id, sortOrder: sort?.sortOrder });
  };

  const handleSortOrderChange = (sortOrder?: TableSortOption) => {
    onChange({ sortBy: sort?.sortBy, sortOrder });
  };

  return (
    <>
      <OptionsEditorControl
        label="Sort by"
        control={
          <Stack flex={1} spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <SettingsAutocomplete
              fullWidth
              disableClearable
              onChange={handleSortByChange}
              // Enabling the `disableClearable` option as `true` prevents the autocomplete from accepting an initial empty value.
              // However, by omitting a default value, MUI interprets this as an uncontrolled component.
              // To override this behavior, we need to set the value type to `any`.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value={(sortByOptions.find((o) => o.id === sort?.sortBy) ?? null) as any}
              options={sortByOptions}
            />
            {onDelete && (
              <IconButton aria-label="delete sort" size="small" onClick={onDelete}>
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        }
      />

      <OptionsEditorControl
        label="Sort order"
        control={
          <SortAutocomplete<TableSortOption>
            disabled={!sort?.sortBy}
            value={sort?.sortOrder}
            options={TABLE_SORT_OPTIONS}
            onChange={handleSortOrderChange}
            width={onDelete ? SORT_AUTOCOMPLETE_WIDTH : undefined}
          />
        }
      />
    </>
  );
}
